import React from 'react';

import { getZonedDate } from '@eika-infrastruktur/date';
import { useViewType, ViewType } from '@eika/hooks';
import { ModalDialogType } from '@eika/modal';

import { useConfig } from '../../../defaultConfig';
import { useAccountFreeWithdrawals, useAccounts } from '../../../fetchHooks/useAccount';
import { useTransferAccountsContext } from '../../../hooks/useAccountState';
import { useFormContext } from '../../../hooks/useForm';
import { isValidAccountTransferFrom } from '../../../services/accountFilters';
import { useSigningContext } from '../../../signingContext/signingContext';
import filterInvalidAccounts from '../../../utils/filterInvalidAccounts';
import { TransferDesktopFields, TransferMobileFields } from './fields';
import FieldProps from './fields/FieldProps';
import { TransferAction, TransferActionType } from './form/actions';
import { TransferState } from './form/state';
import { TransferError } from './form/validate';

interface Props {
    locked?: boolean;
    shouldFocusFirstElement?: boolean;
    isInModal?: boolean;
    useDesktopPlaceholder?: boolean;
    isActive?: boolean;
    isFocusModal?: boolean;
}

const TransferFields: React.FC<Props> = ({
    locked = false,
    shouldFocusFirstElement = false,
    isInModal = true,
    useDesktopPlaceholder = true,
    isActive = true,
    isFocusModal = false,
}) => {
    const { featureToggles } = useConfig();
    const form = useFormContext<TransferState, TransferAction, TransferError, TransferActionType>();
    const { signingOperationPending } = useSigningContext();

    const { data: allAccounts, ...accountState } = useAccounts();
    const validFromAccounts = allAccounts?.filter(isValidAccountTransferFrom) ?? [];
    const invalidFromAccounts = filterInvalidAccounts({ allAccounts, validAccounts: validFromAccounts });

    const { targetFromAccount } = useTransferAccountsContext();
    const { data: freeWithdrawals, showFreewithdrawal } = useAccountFreeWithdrawals(
        targetFromAccount,
        featureToggles.enableGettingAccountWithdrawalsInPayment,
    );
    const animateModalAs: ModalDialogType | undefined = isInModal ? undefined : 'sub';
    const viewType = useViewType();
    const isMobile = viewType === ViewType.mobile;

    const fieldProps: FieldProps = {
        form,
        locked: locked || signingOperationPending,
        shouldFocusFirstElement,
        freeWithdrawals: {
            data: freeWithdrawals,
            show: showFreewithdrawal,
        },
        date: {
            minDate: getZonedDate(),
        },
        validFromAccounts,
        invalidFromAccounts,
        accountState,
        allAccounts,
        animateModalAs,
        useDesktopPlaceholder,
        isActive,
        isFocusModal,
    };

    return isMobile ? <TransferMobileFields {...fieldProps} /> : <TransferDesktopFields {...fieldProps} />;
};

export default TransferFields;
